/**
 * regnr-country.js — decides whether this visitor is offered registration-number search.
 *
 * Why a script at all: the visitor's own country cannot be resolved server-side. Liquid
 * has no visitor IP, and `localization.country` is the MARKET country, not the visitor's —
 * a country in no active market gets the store's backup region, AT, so it answers AT
 * for a Swedish shopper today. So off a pinned market the page is rendered promising
 * nothing, and this upgrades it.
 *
 * The market decides first. On a market in an offered reg-no country, the server rendered
 * `data-regnr-country` on <html> and lit that plate (snippets/regnr-country.liquid). This then
 * only finishes the wording, with no lookup. Elsewhere the country comes from ValerydGeo
 * (assets/geo.js), one lookup per page shared with every other country-dependent enhancement.
 * It is honoured only for a country in the header's `data-regnr-geo` fallback list.
 *
 * What "offered" means: reg-no lookup only returns anything for the registries the
 * backend actually reaches. Everywhere else we stay quiet about it — a German shopper
 * is not told to search by reg-no, because a German plate finds nothing. The lookup is
 * not disabled, only unadvertised: type a reg-no anyway and it still runs.
 *
 * Failure is silent and safe in one direction: any error, timeout or unknown country
 * leaves the page exactly as rendered — no plate, no reg-no wording.
 */
(() => {
  const ROOT = document.documentElement;

  // Swap an input's placeholder to its reg-no wording. The neutral wording is what the
  // server rendered, so there is nothing to restore if this never runs.
  function upgradePlaceholders() {
    document.querySelectorAll('[data-placeholder-regnr]').forEach((el) => {
      el.placeholder = el.dataset.placeholderRegnr;
    });
  }

  // The reg-no widget carries both wordings on its host rather than on the input,
  // because the input comes from the shared form-field snippet and that component has
  // no business knowing about this feature.
  //
  // `q` as well as `regnr`: the hero's field posts to the search route like the header's
  // does, so its name is the search parameter — the wording it swaps is the same.
  function upgradeWidgets() {
    document.querySelectorAll('[data-regnr-label]').forEach((host) => {
      const input = host.querySelector('input[name="regnr"], input[name="q"]');
      if (!input) return;
      if (host.dataset.regnrPlaceholder) input.placeholder = host.dataset.regnrPlaceholder;

      const label = input.id && host.querySelector('label[for="' + input.id + '"]');
      if (!label) return;
      // Replace the label's own text and nothing else — it also holds the required
      // marker as an element child, which textContent would wipe out.
      const text = Array.prototype.find.call(
        label.childNodes,
        (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim()
      );
      if (text) text.nodeValue = host.dataset.regnrLabel;
    });
  }

  // Point the lookup at the visitor's own registry. Until this runs every form carries
  // SE, the registry known to answer — a country the backend does not know 401s, so the
  // default has to be a working one rather than a neutral one.
  function retargetLookups(country) {
    document.querySelectorAll('input[name="country"]').forEach((el) => {
      el.value = country;
    });
    document.querySelectorAll('predictive-search[data-country], trailer-chip[data-country]').forEach((el) => {
      el.dataset.country = country;
    });
  }

  function apply(country) {
    const plate = document.querySelector('.header__plate[data-plate="' + country + '"]');
    // The rendered plates ARE the list of offered countries — the theme setting that
    // produced them is the single source, so there is no second list to drift from it.
    if (!plate) return;
    plate.setAttribute('data-active', '');
    ROOT.setAttribute('data-regnr-country', country);
    retargetLookups(country);
    upgradePlaceholders();
    upgradeWidgets();
  }

  function start() {
    const pinned = ROOT.getAttribute('data-regnr-country');
    if (pinned) return apply(pinned);
    const host = document.querySelector('[data-regnr-geo]');
    const allowed = String((host && host.dataset.regnrGeo) || '')
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
    const geo = window.ValerydGeo;
    if (!allowed.length || !geo) return; // no fallback list → no lookup, nothing offered
    geo.resolve(host.dataset.geoEndpoint).then((result) => {
      if (result && allowed.includes(result.country)) apply(result.country);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
